//
//
//  Dashboard
//
//

import {Flex, LoadingOverlay, rem, Title} from "@mantine/core";
import {
    Card,
    Metric,
    Text as TremorText,
    Grid,
    AreaChart,
    Title as TremorTitle,
    TabGroup,
    TabList,
    Tab,
    DateRangePicker,
    DateRangePickerValue,
    Subtitle as TremorSubtitle,
} from "@tremor/react";
import {useEffect, useState} from "react";
import Api from "../api.ts";
import {
  findObjectByValue,
  formatDate,
  getDayInterval,
  getMonthInterval,
  getWeekInterval,
} from "../utils.ts";
import { useAccessToken } from "../hooks/useAccessToken";

function Dashboard() {
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 1);
  const startDate = new Date();
  startDate.setMonth(startDate.getMonth() - 1);

  const accessToken = useAccessToken(); // aquí obtienes el token directamente
  const [conversationsByDateAndStatusRange, setConversationsByDateAndStatusRange] =
    useState<DateRangePickerValue>({ from: startDate, to: endDate });
  const [conversationsByDateRange, setConversationsByDateRange] =
    useState<DateRangePickerValue>({ from: startDate, to: endDate });
  const [conversationsByDateAndStatus, setConversationsByDateAndStatus] =
    useState<Array<any>>([]);
  const [conversationsByDate, setConversationsByDate] = useState<Array<any>>([]);
  const [conversationsByStatusIndex, setConversationsByStatusIndex] =
    useState<number>(0);
  const [conversationsIndex, setConversationsIndex] = useState(0);
  const [allConversations, setAllConversations] = useState<number | null>(null);
  const [last365DaysConversations, setLast365DaysConversations] =
    useState<number | null>(null);
  const [last30DaysConversations, setLast30DaysConversations] =
    useState<number | null>(null);
  const [last7DaysConversations, setLast7DaysConversations] =
    useState<number | null>(null);
  const [last24HoursConversations, setLast24HoursConversations] =
    useState<number | null>(null);

  // fetch stats total conversations
  async function fetchStatsTotalConversations(token: string) {
    Api.getStatsTotalConversations(token).then((response) => {
      setAllConversations(response["total"]);
    });
    Api.getStatsTotalConversations(token, "LAST_365_DAYS").then((response) => {
      setLast365DaysConversations(response["total"]);
    });
    Api.getStatsTotalConversations(token, "LAST_30_DAYS").then((response) => {
      setLast30DaysConversations(response["total"]);
    });
    Api.getStatsTotalConversations(token, "LAST_7_DAYS").then((response) => {
      setLast7DaysConversations(response["total"]);
    });
    Api.getStatsTotalConversations(token, "LAST_24_HOURS").then((response) => {
      setLast24HoursConversations(response["total"]);
    });
  }

  useEffect(() => {
    if (accessToken) {
      fetchStatsTotalConversations(accessToken).catch(console.error);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    const freqByIndex = ["DAILY", "WEEKLY", "MONTHLY"];

    Api.getStatsTotalConversationsByDate(
      accessToken,
      freqByIndex[conversationsIndex],
      conversationsByDateRange["from"],
      conversationsByDateRange["to"]
    ).then((response) => {
      if (response.length > 1) {
        const endDate: Date = conversationsByDateRange["to"]!;
        const startDate: Date = conversationsByDateRange["from"]!;

        let dates: Array<Date>;
        if (conversationsIndex == 0) {
          // DAILY
          dates = getDayInterval(startDate, endDate);
        } else if (conversationsIndex == 1) {
          // WEEKLY
          dates = getWeekInterval(startDate, endDate);
        } else if (conversationsIndex == 2) {
          // MONTHLY
          dates = getMonthInterval(startDate, endDate);
        } else {
          throw new Error("Unexpected index");
        }

        const newData = dates.map((date: Date) => {
          const dateStr = formatDate(date);
          const itemVal = findObjectByValue(response, "date", dateStr);

          if (itemVal == null) {
            return {
              date: dateStr,
              total: 0,
            };
          } else {
            return itemVal;
          }
        });

        setConversationsByDate(newData);
      } else {
        setConversationsByDate(response.toReversed());
      }
    });
  }, [conversationsIndex, conversationsByDateRange, accessToken]);

  useEffect(() => {
    if (!accessToken) return;

    const freqByIndex = ["DAILY", "WEEKLY", "MONTHLY"];

    Api.getStatsTotalConversationsByDateAndStatus(
      accessToken,
      freqByIndex[conversationsByStatusIndex],
      conversationsByDateAndStatusRange["from"],
      conversationsByDateAndStatusRange["to"]
    ).then((response) => {
      if (response.length > 1) {
        const endDate: Date = conversationsByDateAndStatusRange["to"]!;
        const startDate: Date = conversationsByDateAndStatusRange["from"]!;

        let dates: Array<Date>;
        if (conversationsByStatusIndex == 0) {
          // DAILY
          dates = getDayInterval(startDate, endDate);
        } else if (conversationsByStatusIndex == 1) {
          // WEEKLY
          dates = getWeekInterval(startDate, endDate);
        } else if (conversationsByStatusIndex == 2) {
          // MONTHLY
          dates = getMonthInterval(startDate, endDate);
        } else {
          throw new Error("Unexpected index");
        }

        const newData = dates.map((date: Date) => {
          const dateStr = formatDate(date);
          const itemVal = findObjectByValue(response, "date", dateStr);

          if (itemVal == null) {
            return {
              date: dateStr,
              delivered: 0,
              empty_conversation: 0,
              in_progress: 0,
              pending_identifier: 0,
              pending_transcript: 0,
              pending_summary: 0,
              pending_delivery: 0,              
            };
          } else {
            return itemVal;
          }
        });

        setConversationsByDateAndStatus(newData);
      } else {
        setConversationsByDateAndStatus(response.toReversed());
      }
    });
  }, [conversationsByStatusIndex, conversationsByDateAndStatusRange, accessToken]);

  const totalConversationsByDate = conversationsByDate.reduce(
    (accumulator, currentEntry) => accumulator + currentEntry.total,
    0
  );

  const deliveredConversationsByDate = conversationsByDateAndStatus.reduce(
    (accumulator, currentEntry) => accumulator + currentEntry.delivered,
    0
  );

  // const emptyConversationsByDate = conversationsByDateAndStatus.reduce(
  //   (accumulator, currentEntry) => accumulator + currentEntry.empty,
  //   0
  // );

  // const inProgressConversationsByDate = conversationsByDateAndStatus.reduce(
  //   (accumulator, currentEntry) => accumulator + currentEntry.in_progress,
  //   0
  // );

  const pendingIdentifierConversationsByDate = conversationsByDateAndStatus.reduce(
    (accumulator, currentEntry) => accumulator + currentEntry.pending_identifier,
    0
  );
  const pendingTranscriptConversationsByDate = conversationsByDateAndStatus.reduce(
    (accumulator, currentEntry) => accumulator + currentEntry.pending_transcript,
    0
  );
  const pendingSummaryConversationsByDate = conversationsByDateAndStatus.reduce(
    (accumulator, currentEntry) => accumulator + currentEntry.pending_summary,
    0
  );
  const pendingDeliveryConversationsByDate = conversationsByDateAndStatus.reduce(
    (accumulator, currentEntry) => accumulator + currentEntry.pending_delivery,
    0
  );

  return (
    <>
      <Flex mt={rem(50)} mb={rem(30)}>
        <Title size="h1">Dashboard</Title>
      </Flex>
      <Grid numItemsMd={2} numItemsLg={3} className="gap-6 mb-10">
        <Card>
          <LoadingOverlay visible={allConversations === null} zIndex={1000} />
          <TremorText>Conversations</TremorText>
          <Metric>{allConversations === null ? "" : allConversations}</Metric>
        </Card>
        <Card>
          <LoadingOverlay visible={last365DaysConversations === null} zIndex={1000} />
          <TremorText>Conversations - Last 365 days</TremorText>
          <Metric>{last365DaysConversations === null ? "" : last365DaysConversations}</Metric>
        </Card>
        <Card>
          <LoadingOverlay visible={last30DaysConversations === null} zIndex={1000} />
          <TremorText>Conversations - Last 30 days</TremorText>
          <Metric>{last30DaysConversations === null ? "" : last30DaysConversations}</Metric>
        </Card>
        <Card>
          <LoadingOverlay visible={last7DaysConversations === null} zIndex={1000} />
          <TremorText>Conversations - Last 7 days</TremorText>
          <Metric>{last7DaysConversations === null ? "" : last7DaysConversations}</Metric>
        </Card>
        <Card>
          <LoadingOverlay visible={last24HoursConversations === null} zIndex={1000} />
          <TremorText>Conversations - Last 24 hours</TremorText>
          <Metric>{last24HoursConversations === null ? "" : last24HoursConversations}</Metric>
        </Card>
      </Grid>
      <Card>
        <Flex justify="space-between" align="center" className="w-full">
          <Flex direction="column">
            <TremorTitle>Conversations</TremorTitle>
            <TremorSubtitle>{totalConversationsByDate}</TremorSubtitle>
          </Flex>
          <DateRangePicker
            weekStartsOn={1}
            enableClear={false}
            value={conversationsByDateRange}
            onValueChange={(newValue) => setConversationsByDateRange(newValue)}
          />
          <TabGroup
            className="w-auto"
            index={conversationsIndex}
            onIndexChange={(newIndex) => setConversationsIndex(newIndex)}
          >
            <TabList color="gray" variant="solid">
              <Tab>Daily</Tab>
              <Tab>Weekly</Tab>
              <Tab>Monthly</Tab>
            </TabList>
          </TabGroup>
        </Flex>
        <AreaChart
          className="h-72 mt-4"
          data={conversationsByDate}
          index="date"
          categories={["total"]}
          showLegend={false}
          colors={["indigo"]}
          allowDecimals={false}
        />
      </Card>
      <Card className="mt-10">
        <Flex justify="space-between" align="center" className="w-full">
          <Flex direction="column">
            <TremorTitle>Conversations by status</TremorTitle>
            <TremorSubtitle>Delivered: {deliveredConversationsByDate}</TremorSubtitle>
            <TremorSubtitle>Pending Identifier: {pendingIdentifierConversationsByDate}</TremorSubtitle>
            <TremorSubtitle>Pending Transcript: {pendingTranscriptConversationsByDate}</TremorSubtitle>
            <TremorSubtitle>Pending Summary: {pendingSummaryConversationsByDate}</TremorSubtitle>
            <TremorSubtitle>Pending Delivery: {pendingDeliveryConversationsByDate}</TremorSubtitle>            
          </Flex>
          <DateRangePicker
            weekStartsOn={1}
            enableClear={false}
            value={conversationsByDateAndStatusRange}
            onValueChange={(newValue) => setConversationsByDateAndStatusRange(newValue)}
          />
          <TabGroup
            className="w-auto"
            index={conversationsByStatusIndex}
            onIndexChange={(newIndex) => setConversationsByStatusIndex(newIndex)}
          >
            <TabList color="gray" variant="solid">
              <Tab>Daily</Tab>
              <Tab>Weekly</Tab>
              <Tab>Monthly</Tab>
            </TabList>
          </TabGroup>
        </Flex>
        <AreaChart
          className="h-72 mt-4"
          data={conversationsByDateAndStatus}
          index="date"
          categories={["delivered", "pending identifier", "pending transcript", "pending summary", "pending delivery"]}
          colors={["emerald", "sky", "orange", "red", "purple"]}
          allowDecimals={false}
        />
      </Card>
    </>
  );
}

export default Dashboard
